/**
 * Item de tarea vencida/por vencer en la campanita.
 *
 *  - Deslizar a la derecha → Recordar luego (abre quick-pick de snooze).
 *  - Deslizar a la izquierda → Quitar (dismiss local, no borra la tarea).
 */
import { AlarmClock, Clock, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { SwipeableRow } from '@/components/ui/swipeable-row';
import { SNOOZE_PRESETS } from '@/features/tasks/lib/snoozePresets';
import { cn } from '@/lib/utils';
import type { MyDueTasksRow } from '@/types/database.types';

interface DueTaskItemProps {
  task: MyDueTasksRow;
  onNavigate: () => void;
  /** Quitar de la campanita (dismiss local). */
  onDismiss: () => void;
  /** Crear recordatorio para la tarea en `remindAt` (ISO) y sacarla de la campanita. */
  onSnooze: (remindAtIso: string) => void;
}

export function DueTaskItem({ task, onNavigate, onDismiss, onSnooze }: DueTaskItemProps) {
  const [snoozing, setSnoozing] = useState(false);

  const isOverdue = task.urgency === 'overdue';
  const isDueToday = task.urgency === 'due_today';
  const isDueSoon = task.urgency === 'due_soon';

  /*
   * Subtítulo basado en minutos hasta vencer (vista v_my_due_tasks lo
   * provee). Si vence en < 60 min → muestra minutos; entre 1-48 h → horas;
   * más allá → días. Cuando es overdue, "Venció hace X". Si es hoy y aún
   * no venció, muestra la hora exacta (ej: "Hoy 15:30").
   */
  const mins = task.minutes_until_due ?? 0;
  const dueDate = new Date(task.due_at ?? 0);
  const subtitle = (() => {
    if (isOverdue) {
      const abs = Math.abs(mins);
      if (abs < 60) return `Venció hace ${abs} min`;
      if (abs < 60 * 48) return `Venció hace ${Math.round(abs / 60)} h`;
      return `Venció hace ${Math.round(abs / (60 * 24))} d`;
    }
    if (isDueToday) {
      const hh = String(dueDate.getHours()).padStart(2, '0');
      const mm = String(dueDate.getMinutes()).padStart(2, '0');
      if (mins > 60) return `Vence hoy ${hh}:${mm}`;
      if (mins > 0) return `Vence en ${mins} min · ${hh}:${mm}`;
      return `Vence hoy ${hh}:${mm}`;
    }
    if (mins < 60 * 48) return `Vence en ${Math.round(mins / 60)} h`;
    return `Vence en ${Math.round(mins / (60 * 24))} d`;
  })();

  // Quick-pick de snooze · reemplaza la fila mientras está activo.
  if (snoozing) {
    return (
      <li className="border-border-dim bg-surface-hi/40 border-b last:border-0">
        <div className="flex items-center gap-1.5 px-3.5 py-2.5">
          <AlarmClock className="text-primary size-3.5 shrink-0" />
          <span className="text-fg-dim shrink-0 text-micro">
            Recordar
          </span>
          <div className="flex flex-1 flex-wrap gap-1">
            {SNOOZE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => {
                  onSnooze(p.at());
                  setSnoozing(false);
                }}
                className="border-border-dim bg-surface hover:border-primary/50 hover:text-foreground text-fg-muted rounded-md border px-2 py-1 text-micro font-medium transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSnoozing(false)}
            aria-label="Cancelar recordatorio"
            className="text-fg-dim hover:text-foreground grid size-6 shrink-0 place-items-center rounded-md transition-colors"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="border-border-dim border-b last:border-0">
      <SwipeableRow
        contentClassName="bg-popover"
        hoverActionBg="var(--popover)"
        startAction={{
          icon: AlarmClock,
          label: 'Recordar',
          bg: 'bg-amber-500/90',
          onAction: () => setSnoozing(true),
        }}
        endAction={{
          icon: Trash2,
          label: 'Quitar',
          bg: 'bg-destructive',
          onAction: onDismiss,
          destructive: true,
        }}
      >
        <Link
          to={`/tareas/${task.id}`}
          onClick={onNavigate}
          className="hover:bg-surface-hi/60 flex items-start gap-3 px-3.5 py-2.5 transition-colors"
        >
          <div
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
              isOverdue && 'bg-rose-500/15 text-rose-300',
              isDueToday && 'bg-amber-500/15 text-amber-300',
              isDueSoon && 'bg-sky-500/15 text-sky-300',
              !isOverdue && !isDueToday && !isDueSoon && 'bg-muted text-fg-muted',
            )}
          >
            {isOverdue ? (
              <AlarmClock className="h-3.5 w-3.5" />
            ) : (
              <Clock className="h-3.5 w-3.5" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-foreground truncate text-caption font-semibold">
                {task.title}
              </p>
              <time className="text-fg-dim text-micro">
                {dueDate.toLocaleDateString('es-AR', {
                  day: '2-digit',
                  month: 'short',
                })}
                {' · '}
                {String(dueDate.getHours()).padStart(2, '0')}:
                {String(dueDate.getMinutes()).padStart(2, '0')}
              </time>
            </div>
            <p
              className={cn(
                'mt-0.5 text-micro leading-snug',
                isOverdue ? 'text-rose-300' : 'text-muted-foreground',
              )}
            >
              {subtitle}
            </p>
          </div>
        </Link>
      </SwipeableRow>
    </li>
  );
}
